import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  Divider,
  useToast,
  Grid,
  GridItem,
  IconButton,
  Card,
  CardBody,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { digitalFilesService } from "../digitalFilesService";

const PersonalInfoModal = ({ isOpen, onClose, employee, onSuccess }) => {
  // Dark mode colors
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const bgColor = useColorModeValue("white", "gray.900");

  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      gender: "",
      maritalStatus: "",
      nationality: "",
    },
    address: {
      permanent: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
      },
      current: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
      },
    },
    emergencyContacts: [],
    familyMembers: [],
    education: [],
    healthInfo: {
      bloodGroup: "",
      allergies: "",
      medicalConditions: "",
      emergencyMedicalInfo: "",
    },
    bankDetails: {
      bankName: "",
      accountNumber: "",
      ifscCode: "",
      accountType: "",
    },
    taxInfo: {
      panNumber: "",
      aadharNumber: "",
      taxSlab: "",
    },
    preferences: {
      communicationPreference: "",
      workPreference: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (employee) {
      // If editing existing personal info
      if (employee.personalInfo) {
        setFormData(employee.personalInfo);
      } else {
        // Pre-fill with basic employee data
        setFormData((prev) => ({
          ...prev,
          personalInfo: {
            ...prev.personalInfo,
            firstName: employee.name?.split(" ")[0] || "",
            lastName: employee.name?.split(" ").slice(1).join(" ") || "",
            email: employee.email || "",
            phone: employee.phone || "",
          },
        }));
      }
    }
  }, [employee]);

  const handleSubmit = async () => {
    if (!employee) return;

    try {
      setLoading(true);
      await digitalFilesService.personalInfo.createOrUpdatePersonalInfo(
        employee.employeeId,
        formData
      );

      toast({
        title: "Personal information saved",
        status: "success",
        duration: 3000,
      });

      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: "Error saving personal information",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (path, value) => {
    setFormData((prev) => {
      const keys = path.split(".");
      const lastKey = keys.pop();
      const target = keys.reduce(
        (obj, key) => (obj[key] = obj[key] || {}),
        prev
      );
      target[lastKey] = value;
      return { ...prev };
    });
  };

  const addArrayItem = (arrayName, template = {}) => {
    setFormData((prev) => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), template],
    }));
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index),
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "6xl" }}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor}>
        <ModalHeader px={{ base: 4, md: 6 }}>
          {employee?.personalInfo ? "Edit" : "Add"} Personal Information
          {employee && (
            <Text fontSize="sm" color={mutedColor}>
              for {employee.name}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 4, md: 6 }}>
          <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            {/* Personal Information */}
            <Card>
              <CardBody>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Personal Information
                </Text>
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={4}
                >
                  <FormControl>
                    <FormLabel>First Name</FormLabel>
                    <Input
                      value={formData.personalInfo.firstName}
                      onChange={(e) =>
                        updateFormData("personalInfo.firstName", e.target.value)
                      }
                      minH="44px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Last Name</FormLabel>
                    <Input
                      value={formData.personalInfo.lastName}
                      onChange={(e) =>
                        updateFormData("personalInfo.lastName", e.target.value)
                      }
                      minH="44px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={formData.personalInfo.email}
                      onChange={(e) =>
                        updateFormData("personalInfo.email", e.target.value)
                      }
                      minH="44px"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Phone</FormLabel>
                    <Input
                      value={formData.personalInfo.phone}
                      onChange={(e) =>
                        updateFormData("personalInfo.phone", e.target.value)
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Date of Birth</FormLabel>
                    <Input
                      type="date"
                      value={formData.personalInfo.dateOfBirth}
                      onChange={(e) =>
                        updateFormData(
                          "personalInfo.dateOfBirth",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Gender</FormLabel>
                    <Select
                      value={formData.personalInfo.gender}
                      onChange={(e) =>
                        updateFormData("personalInfo.gender", e.target.value)
                      }
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Marital Status</FormLabel>
                    <Select
                      value={formData.personalInfo.maritalStatus}
                      onChange={(e) =>
                        updateFormData(
                          "personalInfo.maritalStatus",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Status</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Nationality</FormLabel>
                    <Input
                      value={formData.personalInfo.nationality}
                      onChange={(e) =>
                        updateFormData(
                          "personalInfo.nationality",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                </Grid>
              </CardBody>
            </Card>

            {/* Address Information */}
            <Card>
              <CardBody>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Address Information
                </Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                  <Box>
                    <Text fontWeight="semibold" mb={3}>
                      Permanent Address
                    </Text>
                    <VStack spacing={3}>
                      <Input
                        placeholder="Street Address"
                        value={formData.address.permanent.street}
                        onChange={(e) =>
                          updateFormData(
                            "address.permanent.street",
                            e.target.value
                          )
                        }
                      />
                      <HStack spacing={3}>
                        <Input
                          placeholder="City"
                          value={formData.address.permanent.city}
                          onChange={(e) =>
                            updateFormData(
                              "address.permanent.city",
                              e.target.value
                            )
                          }
                        />
                        <Input
                          placeholder="State"
                          value={formData.address.permanent.state}
                          onChange={(e) =>
                            updateFormData(
                              "address.permanent.state",
                              e.target.value
                            )
                          }
                        />
                      </HStack>
                      <HStack spacing={3}>
                        <Input
                          placeholder="ZIP Code"
                          value={formData.address.permanent.zipCode}
                          onChange={(e) =>
                            updateFormData(
                              "address.permanent.zipCode",
                              e.target.value
                            )
                          }
                        />
                        <Input
                          placeholder="Country"
                          value={formData.address.permanent.country}
                          onChange={(e) =>
                            updateFormData(
                              "address.permanent.country",
                              e.target.value
                            )
                          }
                        />
                      </HStack>
                    </VStack>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" mb={3}>
                      Current Address
                    </Text>
                    <VStack spacing={3}>
                      <Input
                        placeholder="Street Address"
                        value={formData.address.current.street}
                        onChange={(e) =>
                          updateFormData(
                            "address.current.street",
                            e.target.value
                          )
                        }
                      />
                      <HStack spacing={3}>
                        <Input
                          placeholder="City"
                          value={formData.address.current.city}
                          onChange={(e) =>
                            updateFormData(
                              "address.current.city",
                              e.target.value
                            )
                          }
                        />
                        <Input
                          placeholder="State"
                          value={formData.address.current.state}
                          onChange={(e) =>
                            updateFormData(
                              "address.current.state",
                              e.target.value
                            )
                          }
                        />
                      </HStack>
                      <HStack spacing={3}>
                        <Input
                          placeholder="ZIP Code"
                          value={formData.address.current.zipCode}
                          onChange={(e) =>
                            updateFormData(
                              "address.current.zipCode",
                              e.target.value
                            )
                          }
                        />
                        <Input
                          placeholder="Country"
                          value={formData.address.current.country}
                          onChange={(e) =>
                            updateFormData(
                              "address.current.country",
                              e.target.value
                            )
                          }
                        />
                      </HStack>
                    </VStack>
                  </Box>
                </Grid>
              </CardBody>
            </Card>

            {/* Emergency Contacts */}
            <Card>
              <CardBody>
                <HStack justify="space-between" mb={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    Emergency Contacts
                  </Text>
                  <Button
                    size="sm"
                    leftIcon={<FiPlus />}
                    onClick={() =>
                      addArrayItem("emergencyContacts", {
                        name: "",
                        relationship: "",
                        phone: "",
                        email: "",
                      })
                    }
                  >
                    Add Contact
                  </Button>
                </HStack>
                <VStack spacing={3}>
                  {formData.emergencyContacts.map((contact, index) => (
                    <Card key={index} size="sm" w="full">
                      <CardBody>
                        <Grid
                          templateColumns="repeat(4, 1fr) auto"
                          gap={3}
                          alignItems="center"
                        >
                          <Input
                            placeholder="Name"
                            value={contact.name}
                            onChange={(e) =>
                              updateArrayItem(
                                "emergencyContacts",
                                index,
                                "name",
                                e.target.value
                              )
                            }
                          />
                          <Input
                            placeholder="Relationship"
                            value={contact.relationship}
                            onChange={(e) =>
                              updateArrayItem(
                                "emergencyContacts",
                                index,
                                "relationship",
                                e.target.value
                              )
                            }
                          />
                          <Input
                            placeholder="Phone"
                            value={contact.phone}
                            onChange={(e) =>
                              updateArrayItem(
                                "emergencyContacts",
                                index,
                                "phone",
                                e.target.value
                              )
                            }
                          />
                          <Input
                            placeholder="Email"
                            value={contact.email}
                            onChange={(e) =>
                              updateArrayItem(
                                "emergencyContacts",
                                index,
                                "email",
                                e.target.value
                              )
                            }
                          />
                          <IconButton
                            icon={<FiTrash2 />}
                            colorScheme="red"
                            variant="ghost"
                            onClick={() =>
                              removeArrayItem("emergencyContacts", index)
                            }
                          />
                        </Grid>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              </CardBody>
            </Card>

            {/* Health Information */}
            <Card>
              <CardBody>
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Health Information
                </Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl>
                    <FormLabel>Blood Group</FormLabel>
                    <Select
                      value={formData.healthInfo.bloodGroup}
                      onChange={(e) =>
                        updateFormData("healthInfo.bloodGroup", e.target.value)
                      }
                    >
                      <option value="">Select Blood Group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Allergies</FormLabel>
                    <Input
                      value={formData.healthInfo.allergies}
                      onChange={(e) =>
                        updateFormData("healthInfo.allergies", e.target.value)
                      }
                    />
                  </FormControl>
                  <FormControl gridColumn="span 2">
                    <FormLabel>Medical Conditions</FormLabel>
                    <Textarea
                      value={formData.healthInfo.medicalConditions}
                      onChange={(e) =>
                        updateFormData(
                          "healthInfo.medicalConditions",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl gridColumn="span 2">
                    <FormLabel>Emergency Medical Info</FormLabel>
                    <Textarea
                      value={formData.healthInfo.emergencyMedicalInfo}
                      onChange={(e) =>
                        updateFormData(
                          "healthInfo.emergencyMedicalInfo",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                </Grid>
              </CardBody>
            </Card>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading}>
            Save Personal Information
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PersonalInfoModal;
